import type {
	MessengerTicket,
	WorkerResponse,
	WorkerTicket,
	MessengerResponse,
	MessengerHandlers,
} from "../@types/Types";
import {
	JavaFunctionNames,
	JavaFunctionArgs,
	JavaFunctionReturn,
} from "../util/workerUtils";
import {consoleLogSingleton, ConsoleLog} from "../util/consoleLogUtils";
import {FeatureFlagStore} from "../state/featureFlagStore";

// @ts-ignore This file is gitignored, but generated by npm run build/watch
import {workerJs} from "../../worker-js";

export type MessengerProps = {
	messengerHandlers: MessengerHandlers;
	onTicketResponse?: <FuncName extends JavaFunctionNames>(
		response: WorkerResponse<FuncName>
	) => Promise<void> | void;
	onConsoleLogLine: (log: string) => void;
	featureFlagStore: FeatureFlagStore;
};

export class Messenger {
	// Whether the worker is ready to do things
	// Initializing code, resetting, updating will affect this
	private _initialized = false;

	// The log object that will print to the console
	private _log: ConsoleLog;
	private _onConsoleLog: (log: string) => void; // Will be called when a line is added to the log

	private _worker?: Worker; // undefined if not yet initialized

	// Whether the worker has been terminated
	private _workerTerminated = false;

	// Called when a response is received from the worker
	// Currently it just does some basic error handling
	private _onTicketResponse: <FuncName extends JavaFunctionNames>(
		response: WorkerResponse<FuncName>
	) => Promise<void> | void;

	// A map of ticket ids to their resolvers
	// When a response is received, the resolver is called with the response
	private _ticketResolvers: Record<
		number,
		(arg: WorkerResponse<JavaFunctionNames>) => void
	> = {};

	// Functions that will be called when a request originating from the worker is received
	private _messengerHandlers: MessengerHandlers;

	// The next ticket id to be sent to the worker
	private _nextTicketId = 1;

	// Whether the worker has been paused
	private _pendingStopTheaterAcknowledgement = false;

	// The feature flag store
	// This is used to check if bjvm is enabled
	private _featureFlagStore: FeatureFlagStore;

	constructor(props: MessengerProps) {
		this._log = consoleLogSingleton;
		this._onConsoleLog = props.onConsoleLogLine;

		this._onTicketResponse = props.onTicketResponse ?? (() => {});

		this._messengerHandlers = props.messengerHandlers;

		this._featureFlagStore = props.featureFlagStore;

		this.initWorker();
	}

	/**
	 * Initialize worker, via serverUrl if necessary.  Does NOT need to be awaited.
	 *
	 * Due to CORS issues, we can't load the worker directly from the server, so
	 * instead we fetch the code and load it in via blob.  Server fetching means
	 * async; however, we want to be able to use the messenger asap.  So we use
	 * an array of resolve() functions to signal when the worker is ready.
	 */
	private initWorker() {
		// Try to create the worker
		try {
			const fetchText = workerJs;
			// Remove bundle's .map file reference since it doesn't exist locally
			const fetchTextNoMap = fetchText.replace(
				/\/\/# sourceMappingURL=.*$/,
				""
			);
			const blob = new Blob([fetchTextNoMap], {
				type: "application/javascript",
			});
			this._worker = new Worker(URL.createObjectURL(blob));

			console.info("blob-based worker creation successful");
		} catch (e) {
			// If it fails we have no way to recover, so just throw below
		}

		// the worker failed somehow???  TODO @tim handle this in v2
		if (!this._worker) {
			throw new Error("Worker creation failed");
		}
		this._attachWorkerListeners();

		this._awaitWorkerResponse({
			func: "workerThreadSetSharedArray",
			args: [this._log.getSab()],
		});
	}

	/**
	 * Returns a promise that resolves with the result of the worker's response.
	 *
	 * Use this when defining your apis.
	 */
	private _awaitWorkerResponse<FuncName extends JavaFunctionNames>(
		funcProps: {func: FuncName; args: JavaFunctionArgs<FuncName>},
		view?: OffscreenCanvas
	): Promise<JavaFunctionReturn<FuncName>> {
		return new Promise((resolve) => {
			const ticketId = this._sendTicketToWorker(funcProps, view);
			if (ticketId) {
				this._ticketResolvers[ticketId] = async (response) => {
					await this._onTicketResponse(response);
					resolve(response.result as JavaFunctionReturn<FuncName>);
				};
			} else {
				// Should never reach here unless worker has been terminated
				throw new Error("Worker terminated");
			}
		});
	}

	destroyWorker() {
		this._worker?.terminate();
		this._workerTerminated = true;
	}

	sendStudentInput(id: number, input: string | null) {
		const ticket: MessengerResponse<"sendStudentInput"> = {
			id: id,
			timestamp: Date.now(),
			func: "sendStudentInput",
			result: [input],
		};
		this._sendRawTicketToWorker(ticket);
	}

	getActorMethods(x: number, y: number) {
		return this._awaitWorkerResponse({
			func: "getActorMethods",
			args: [x, y],
		});
	}

	invokeActorMethod(jsonString: string) {
		return this._awaitWorkerResponse({
			func: "invokeActorMethod",
			args: [jsonString],
		});
	}

	async resetTheater() {
		this._initialized = false;

		const result = await this._awaitWorkerResponse({
			func: "resetTheater",
			args: [],
		});
		this._initialized = true;
		return result;
	}

	async updateTheater(inputJson: string, options?: {forceUpdate?: boolean}) {
		if (!options?.forceUpdate && this._pendingStopTheaterAcknowledgement)
			return; // theater is stopped -- refuse to send more updates

		if (!this._initialized) {
			console.error("Not ready");
			return;
		}
		return this._awaitWorkerResponse({
			func: "updateTheater",
			args: [inputJson],
		});
	}

	updateMultiplayerTheater(updateJson: string) {
		return this._awaitWorkerResponse({
			func: "updateMultiplayerTheater",
			args: [updateJson],
		});
	}

	runActivityTests(project: string) {
		return this._awaitWorkerResponse({
			func: "runActivityTests",
			args: [project],
		});
	}

	private _attachWorkerListeners() {
		if (this._workerTerminated || !this._worker) {
			return;
		}

		setInterval(() => {
			if (this._log.logBufferHasLines()) {
				const startTime = performance.now();
				let i = 0;
				for (const p of this._log.dequeueLines()) {
					if (
						++i % 1024 === 0 &&
						performance.now() - startTime > 10 /* don't block too long */
					) {
						break;
					}

					this._onConsoleLog(p);
				}
			}
		}, 10);

		this._worker.onmessage = (e: MessageEvent) => {
			const response:
				| WorkerResponse<JavaFunctionNames>
				| WorkerTicket<JavaFunctionNames> = e.data;

			// This checks if the ticket is a request originating from the worker
			if ("args" in response) {
				if (response.func in this._messengerHandlers) {
					// @ts-ignore
					// I'm pretty sure this is narrowed down enough that it should be fine
					this._messengerHandlers[response.func](...response.args);
				} else {
					console.error("MessengerHandler: Unknown function", response.func);
				}
				return;
			}

			// If the the id corresponds to a ticket, resolve it
			// then delete the resolver from the map
			const resolveTicket = this._ticketResolvers[response.id];
			if (!resolveTicket) {
				console.error("Unknown ticket id: ", response.id);
				return;
			}
			resolveTicket(response);
			delete this._ticketResolvers[response.id];
		};
	}

	async loadCode(jarData: Uint8Array, version: number) {
		// wait until we're certain we are, or are not, using bjvm. (loadCode triggers the JVM to load.)
		await this._featureFlagStore.flagsReady();

		if (this._featureFlagStore.bjvmEnabled) {
			this._awaitWorkerResponse({
				// the worker backend will now use bjvm instead of (default) CheerpJ
				func: "enableBjvm",
				args: [window.location.origin], // required for bjvm to fetch runtime files
			});
		}

		const result = await this._awaitWorkerResponse({
			func: "loadCode",
			args: [jarData, version],
		});
		this._initialized = true;
		return result;
	}

	async initTheater(options: {
		width: number;
		height: number;
		view: OffscreenCanvas;
	}) {
		const result = await this._awaitWorkerResponse(
			{
				func: "initTheater",
				args: [options],
			},
			options.view
		);
		this._initialized = true;
		return result;
	}

	async destroyTheater() {
		const result = await this._awaitWorkerResponse({
			func: "destroyTheater",
			args: [],
		});
		this._initialized = false;
		return result;
	}

	async playTheater() {
		return this._awaitWorkerResponse({
			func: "playTheater",
			args: [],
		});
	}

	async pauseTheater() {
		this._pendingStopTheaterAcknowledgement = true;
		const result = await this._awaitWorkerResponse({
			func: "pauseTheater",
			args: [],
		});
		this._pendingStopTheaterAcknowledgement = false;
		return result;
	}

	clearLog() {
		this._log.mainThreadClearLog();
	}

	/**
	 * You should NOT call this method directly. Use _awaitWorkerResponse instead.
	 *
	 * This method is used to send a ticket to the worker.
	 * @returns the ticket id.
	 */
	private _sendTicketToWorker<FuncName extends JavaFunctionNames>(
		funcProps: {func: FuncName; args: JavaFunctionArgs<FuncName>},
		view?: OffscreenCanvas
	) {
		if (this._workerTerminated) {
			return;
		}

		const newTicket: MessengerTicket<FuncName> = {
			id: this._nextTicketId,
			timestamp: Date.now(),
			func: funcProps.func,
			args: funcProps.args,
			status: "Requested",
		};

		this._sendRawTicketToWorker(newTicket, view);

		this._nextTicketId++;
		return newTicket.id;
	}

	/**
	 * You should NOT call this method directly. Use _awaitWorkerResponse instead.
	 * EXCEPTION: sendStudentInput, TODO: Fix that.
	 *
	 * This method is used to send a ticket to the worker.
	 * It will wait for the worker to be ready if it is not.  You do NOT need to
	 * await this method since this._waitingOnWorker will resolve itself when the
	 * worker is ready.
	 */
	private async _sendRawTicketToWorker(
		ticket:
			| MessengerTicket<JavaFunctionNames>
			| MessengerResponse<JavaFunctionNames>,
		view?: OffscreenCanvas
	) {
		// this._worker should be set now; if not, something went seriously wrong
		if (!this._worker) {
			throw new Error("Worker not initialized");
		}

		if (view) {
			// Second argument of postMessage is a list of transferables, which means that ownership of the object
			// is passed to the worker. In this case we're sending an OffscreenCanvas, so the worker can now
			// manipulate it directly, while the main thread can no longer interact with it.
			this._worker.postMessage(ticket, [view]);
			return;
		}
		this._worker.postMessage(ticket);
	}
}
